import React from "react";
import { Col, FormFeedback, Input, Label, Row } from "reactstrap";
import * as Yup from "yup";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import Flatpickr from "react-flatpickr";
import { Russian } from "flatpickr/dist/l10n/ru.js";
import moment from "moment";

import { fetchWrapper } from "@helpers/fetchwrapper";
import SelectAsyncPaginate from "@areas/shared/components/select/SelectAsyncPaginate";

import styles from "@areas/shared/components/personal/modals/styles/customModal.module.css";

const types = [
  { value: 1, label: "Общественное" },
  { value: 2, label: "День открытых дверей" },
  { value: 3, label: "День без турникета" },
];

const initialValues = (event) => {
  const now = moment().format("DD-MM-YYYY");
  const tags = [];

  if (event && event.Tags) {
    event.Tags.forEach((element) => {
      tags.push({ value: element, label: element });
    });
  }

  let company = null;
  if (event && event.IdCompany) {
    company = {
      value: event.IdCompany,
      label:
        event.NameCompany ||
        event.Shortname ||
        event.Fullname ||
        `Компания #${event.IdCompany}`,
    };
  }

  return {
    id: (event && event.Id) || null,
    name: (event && event.Name) || "",
    description: (event && event.Description) || "",
    type: (event && event.IdType) || 1,
    dateStart: (event && moment(event.DateStart).format("DD-MM-YYYY")) || now,
    dateEnd: (event && moment(event.DateEnd).format("DD-MM-YYYY")) || now,
    address: 1,
    tags,
    company,
    idCompany: (event && event.IdCompany) || 0,
  };
};

const validationSchema = () => {
  return Yup.object({
    name: Yup.string().required("Введите наименование"),
    company: Yup.object().nullable().required("Выберите компанию"),
  });
};

const confirm = (values, successFunc) => {
  const sdate = moment(values.dateStart, "DD-MM-YYYY").format("YYYY-MM-DD");
  const edate = moment(values.dateEnd, "DD-MM-YYYY").format("YYYY-MM-DD");
  const idCompany = values.company?.value || values.idCompany;

  const data = new FormData();
  data.append("idType", values.type);
  data.append("name", values.name);
  data.append("description", values.description || "");
  data.append("idCompany", idCompany);
  data.append("dateStart", sdate);
  data.append("dateEnd", edate);

  if (values.tags) {
    values.tags.forEach((element) => {
      data.append("tags[]", element.value);
    });
  }

  if (values.id) {
    data.append("id", values.id);
    fetchWrapper
      .post(`/v1/api/admin/updateevent`, data, false)
      .then(() => successFunc());
  } else {
    fetchWrapper
      .post(`/v1/api/admin/insertevent?idAddress=${values.address}`, data, false)
      .then(() => successFunc());
  }
};

const loadCompaniesOptions = async (searchQuery, loadedOptions, { page }) => {
  const url = `/v1/api/app/findCompanies/?text=${searchQuery}&offset=${
    page * 10
  }&limit=10`;

  const result = await fetchWrapper.post(url, [1, 2, 3, 4]);

  const options = result.data.map((p) => ({
    value: p.Id,
    label: p.Shortname ? p.Shortname : p.Fullname,
  }));

  return {
    options,
    hasMore: result.total > page * 10,
    additional: {
      page: searchQuery ? 1 : page + 1,
    },
  };
};

function ModalBodyAdd({ validation, readOnly = false }) {
  return (
    <Row className="g-2">
      <Input
        type="hidden"
        name="id"
        defaultValue={validation.values.id || null}
      />
      <Input
        type="hidden"
        name="address"
        defaultValue={validation.values.address || 1}
      />
      <Col sm={12}>
        <div>
          <Label htmlFor="eventCompany-field" className={styles.label}>
            Компания
          </Label>
          {readOnly ? (
            <Input
              className={`form-control ${styles.input}`}
              type="text"
              disabled
              value={validation.values.company?.label || ""}
            />
          ) : (
            <SelectAsyncPaginate
              name="company"
              id="eventCompany-field"
              loadFunc={loadCompaniesOptions}
              value={validation.values.company || null}
              placeholder="Найдите и выберите компанию"
              onChangeValue={(e) => {
                validation.setFieldValue("company", e);
                validation.setFieldValue("idCompany", e?.value || 0);
              }}
              onBlurInput={() => validation.setFieldTouched("company")}
            />
          )}
          {validation.touched.company && validation.errors.company ? (
            <div className={`invalid-feedback d-block ${styles.invalid}`}>
              {validation.errors.company}
            </div>
          ) : null}
        </div>
      </Col>
      <Col sm={12}>
        <div>
          <Label htmlFor="eventName-field" className={styles.label}>
            Наименование
          </Label>
          <Input
            name="name"
            id="eventName-field"
            className={`form-control ${styles.input}`}
            placeholder="Введите наименование"
            type="text"
            disabled={readOnly}
            onChange={validation.handleChange}
            onBlur={validation.handleBlur}
            value={validation.values.name || ""}
            invalid={
              validation.touched.name && validation.errors.name ? true : false
            }
          />
          {validation.touched.name && validation.errors.name ? (
            <FormFeedback type="invalid" className={styles.invalid}>
              {validation.errors.name}
            </FormFeedback>
          ) : null}
        </div>
      </Col>
      <Col sm={12}>
        <div>
          <Label htmlFor="eventDescription-field" className={styles.label}>
            Описание
          </Label>
          <Input
            name="description"
            id="eventDescription-field"
            className={`form-control ${styles.input}`}
            placeholder="Введите описание"
            type="textarea"
            rows={4}
            disabled={readOnly}
            onChange={validation.handleChange}
            onBlur={validation.handleBlur}
            value={validation.values.description || ""}
          />
        </div>
      </Col>
      <Col sm={12}>
        <div>
          <Label htmlFor="eventType-field" className={styles.label}>
            Тип
          </Label>
          <Select
            name="type"
            id="eventType-field"
            options={types}
            isDisabled={readOnly}
            value={
              validation.values.type
                ? types.find((p) => p.value === validation.values.type)
                : null
            }
            placeholder="Выберите тип"
            onChange={(e) => {
              validation.setFieldValue("type", e.value);
            }}
            onBlur={() => validation.setFieldTouched("type")}
          />
        </div>
      </Col>
      <Col sm={6}>
        <div>
          <Label htmlFor="eventDateStart-field" className={styles.label}>
            Начало
          </Label>
          <Flatpickr
            id="eventDateStart-field"
            name="dateStart"
            placeholder="Выберите дату"
            className={`form-control ${styles.input}`}
            data-date-format="d.m.Y"
            disabled={readOnly}
            options={{
              locale: Russian,
            }}
            value={validation.values.dateStart || moment().format("DD-MM-YYYY")}
            onChange={([date]) => {
              validation.setFieldValue(
                "dateStart",
                moment(date).format("DD-MM-YYYY")
              );
              validation.setFieldTouched("dateStart");
            }}
          />
        </div>
      </Col>
      <Col sm={6}>
        <div>
          <Label htmlFor="eventDateEnd-field" className={styles.label}>
            Конец
          </Label>
          <Flatpickr
            id="eventDateEnd-field"
            name="dateEnd"
            placeholder="Выберите дату"
            className={`form-control ${styles.input}`}
            data-date-format="d.m.Y"
            disabled={readOnly}
            options={{
              locale: Russian,
            }}
            value={validation.values.dateEnd || moment().format("DD-MM-YYYY")}
            onChange={([date]) => {
              validation.setFieldValue(
                "dateEnd",
                moment(date).format("DD-MM-YYYY")
              );
              validation.setFieldTouched("dateEnd");
            }}
          />
        </div>
      </Col>
      <Col sm={12}>
        <div>
          <Label htmlFor="eventTags-field" className={styles.label}>
            Теги
          </Label>
          <CreatableSelect
            name="tags"
            id="eventTags-field"
            placeholder="Введите теги"
            options={[]}
            isMulti={true}
            isDisabled={readOnly}
            value={validation.values.tags || []}
            onChange={(e) => {
              validation.setFieldValue("tags", e);
            }}
            formatCreateLabel={(value) => `Добавить ${value}`}
            noOptionsMessage={() => "Ничего не найдено"}
            onBlur={() => validation.setFieldTouched("tags")}
          />
        </div>
      </Col>
    </Row>
  );
}

export { initialValues, validationSchema, confirm, ModalBodyAdd, types };
